import { useState, type FormEvent } from 'react';
import { useWhales } from '@/api/hooks';
import type { WhalesCohort, WhalesFilters, WhalesReport } from '@/api/types';
import { dash, formatCount, formatDateTime, formatDay, formatEur, formatPercent } from '@/lib/format';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Field, INPUT_CLASS } from '@/ui/Field';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { Stat } from '@/ui/Stat';
import { totalsOf } from './whale-figures';
import { WhalesTable } from './WhalesTable';

const LIMITS = [50, 100, 200, 500];
const DEFAULTS: WhalesFilters = { cohort: 'top', from: '', to: '', limit: 50 };

const COHORT_LABELS: Record<WhalesCohort, string> = {
  top: 'Top depositors in the range',
  tier1: 'Tier 1 players',
};

function tier1RuleText({ tier1Rule: rule, recentDays }: WhalesReport): string {
  const window = recentDays
    ? `${formatDay(recentDays.from)} – ${formatDay(recentDays.to)} (UTC)`
    : 'the latest three days';
  return `${rule.minDeposits}+ deposits totalling €${rule.minDepositEur}+ (€${rule.latamMinDepositEur}+ for LATAM players) over ${window}.`;
}

function Filters({ coverage, onApply }: { coverage: WhalesReport['coverage']; onApply(filters: WhalesFilters): void }) {
  const [draft, setDraft] = useState<WhalesFilters>(DEFAULTS);
  const set = (name: 'from' | 'to') => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [name]: event.target.value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onApply(draft);
  };

  const reset = () => {
    setDraft(DEFAULTS);
    onApply(DEFAULTS);
  };

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-4" aria-label="Filters">
      <Field id="whales-cohort" label="Players">
        <select
          id="whales-cohort"
          className={INPUT_CLASS}
          value={draft.cohort}
          onChange={(event) => setDraft((current) => ({ ...current, cohort: event.target.value as WhalesCohort }))}
        >
          <option value="top">{COHORT_LABELS.top}</option>
          <option value="tier1">{COHORT_LABELS.tier1}</option>
        </select>
      </Field>
      <Field id="whales-from" label="From (UTC day)" hint="Empty: the last 72 hours, three UTC days up to To.">
        <input
          id="whales-from"
          type="date"
          className={INPUT_CLASS}
          min={coverage?.from}
          max={coverage?.to}
          value={draft.from}
          onChange={set('from')}
        />
      </Field>
      <Field id="whales-to" label="To (UTC day)" hint="Empty: the latest synced day.">
        <input
          id="whales-to"
          type="date"
          className={INPUT_CLASS}
          min={coverage?.from}
          max={coverage?.to}
          value={draft.to}
          onChange={set('to')}
        />
      </Field>
      <Field id="whales-limit" label="How many">
        <select
          id="whales-limit"
          className={INPUT_CLASS}
          value={draft.limit}
          onChange={(event) => setDraft((current) => ({ ...current, limit: Number(event.target.value) }))}
        >
          {LIMITS.map((limit) => (
            <option key={limit} value={limit}>
              Up to {limit}
            </option>
          ))}
        </select>
      </Field>
      <div className="flex items-end gap-2 md:col-span-4">
        <Button type="submit">Apply</Button>
        <Button type="button" variant="secondary" onClick={reset}>
          Reset
        </Button>
      </div>
    </form>
  );
}

function Totals({ report }: { report: WhalesReport }) {
  const totals = totalsOf(report.rows);
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Stat
        label="Deposits"
        value={formatEur(totals.depositEur)}
        hint={`${formatCount(totals.depositCount)} deposits by ${formatCount(totals.players)} players`}
      />
      <Stat
        label="GGR"
        value={formatEur(totals.ggrEur)}
        hint={`Hold ${formatPercent(totals.holdRate)} · ${formatCount(totals.winners)} players won against the house`}
      />
      <Stat label="NGR" value={formatEur(totals.ngrEur)} hint="As the analysts' mart computes it" />
      <Stat
        label="Reward + cashback + bonus"
        value={formatEur(totals.rewardCostEur)}
        hint={
          totals.rewardShare === null
            ? 'No player with positive GGR'
            : `${formatPercent(totals.rewardShare)} of the GGR of players with positive GGR`
        }
      />
    </div>
  );
}

export function WhalesPage() {
  const [filters, setFilters] = useState<WhalesFilters>(DEFAULTS);
  const query = useWhales(filters);

  const report = query.data;
  const synced = report?.coverage ? report : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Whales</h1>
        <p className="text-sm text-slate-600">
          Biggest depositors and Tier 1 players with what they deposited, played and cost us in rewards, and how CRM
          reaches them. Figures are the analysts&apos; mart in EUR, by UTC day, refreshed every 2 hours
          {report?.meta.lastSyncAt ? ` — last refreshed ${formatDateTime(report.meta.lastSyncAt)}` : ''}.
        </p>
      </div>

      <Card title="Filters">
        <Filters coverage={report?.coverage ?? null} onApply={setFilters} />
      </Card>

      {query.isPending && <Loading what="whales" />}
      {query.isError && <ErrorNotice error={query.error} />}
      {report && !synced && (
        <Notice>
          The whales data has not been synced yet. It refreshes every 2 hours; check back after the next run.
        </Notice>
      )}

      {synced && (
        <>
          <Totals report={synced} />
          <Card
            title={COHORT_LABELS[synced.cohort]}
            description={[
              `Money and main game from ${synced.from ? formatDay(synced.from) : dash} to ${synced.to ? formatDay(synced.to) : dash} (UTC).`,
              synced.activity.comms
                ? `Last comm and preferred method: CRM messages since ${formatDay(synced.activity.comms.from)}.`
                : 'Last comm and preferred method: the CRM messages have not been synced yet.',
              `Tier 1: ${tier1RuleText(synced)}`,
              synced.rows.length === synced.limit
                ? `Showing the first ${synced.limit}; raise "How many" to see more.`
                : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <WhalesTable key={`${synced.cohort}-${synced.from}-${synced.to}-${synced.limit}`} report={synced} />
          </Card>
        </>
      )}
    </div>
  );
}
